import React, { useEffect, useState } from 'react';
import { Modal, Button, Form, Row, Col } from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import AsyncSelect from 'react-select/async';
import type { GroupBase } from 'react-select';
import 'react-datepicker/dist/react-datepicker.css';

import apiDefinitions from '../../../../core/api/apiDefinitions';
import { checkPermission } from '../../../../core/permissons/CheckPermissons';
import { showError, showSuccess } from '../../../../core/alert/sweetAlertConfig';

/* ----------------------------- DTOs & Props ----------------------------- */

interface LocationDTO { id: number; name: string; }
interface BuildingDTO { id: number; name: string; }
interface FloorDTO { id: number; name: string; }
interface RoomDTO { id: number; name: string; }

export interface User {
  id: number;
  empNumber: string;
  name: string;
  createdAt: string;
  status: string;
  email: string;
  phone: string;
  userType: string;
  departmentNames: string[];
  licenseNumber: string;
  licenseType: string;
  expireDate: string;
}

type ScheduleType =
  | 'DAILY' | 'WEEKLY' | 'MONTHLY' | 'YEARLY'
  | 'WEEKDAYS' | 'WEEKENDS' | 'CUSTOM';

const SCHEDULE_TYPES: ScheduleType[] = [
  'DAILY', 'WEEKLY', 'MONTHLY', 'YEARLY', 'WEEKDAYS', 'WEEKENDS', 'CUSTOM'
];

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'] as const;
type DayName = typeof DAYS[number];
const WEEKDAY_SET: DayName[] = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];
const WEEKEND_SET: DayName[] = ['Saturday', 'Sunday'];

const OCCURRENCE_UNITS = ['DAYS', 'WEEKS', 'MONTHS', 'YEARS'] as const;
type OccurrenceUnit = typeof OCCURRENCE_UNITS[number];

type TaskStatus = 'ACTIVE' | 'INACTIVE';

interface MaintenanceTaskDTO {
  id: number;
  locationId: number | null;
  buildingId: number | null;
  floorId: number | null;
  roomId: number | null;
  description: string;
  taskScheduleId: number | null;
  startDateTime: string;
  endDateTime: string | null;
  isRecurring?: boolean;
  recurring?: boolean;
  status: string;
  permanentResponsibleAllocate: boolean;
  responsiblePersons: User[];
  accountablePersons: User[];
  scheduleType?: string | null;
  customDays?: string | null;
  customDaysOfWeek?: string | null;
  occurrenceCount?: number | null;
  customType?: OccurrenceUnit | null;
}

interface Props {
  show: boolean;
  onHide: () => void;
  onSuccess: () => void;
  task: MaintenanceTaskDTO;
  locations: LocationDTO[];
}

/* ----------------------------- Helpers: Users ----------------------------- */

type UserOption = { value: string; label: string; user: User };

const mapApiUserToDTO = (u: any): User | null => {
  if (!u) return null;

  const apiId = Number(u?.id ?? u?.empNumber);
  if (Number.isNaN(apiId)) return null;

  let expireDate = '';
  const rawExpire = u?.driverSpecificData?.expireDate;
  if (rawExpire) {
    const d = new Date(rawExpire);
    if (!isNaN(d.getTime())) expireDate = d.toISOString().split('T')[0];
  }

  const deptNames: string[] =
    Array.isArray(u?.departmentAdminSpecificData?.departmentNames)
      ? u.departmentAdminSpecificData.departmentNames
      : [];

  return {
    id: apiId,
    empNumber: String(u.empNumber ?? ''),
    name: u?.name ?? u?.fullName ?? u?.email ?? 'Unknown',
    createdAt: u?.createdAt ? new Date(u.createdAt).toISOString() : '',
    status: String(u?.status ?? '').toUpperCase(),
    email: u?.email ?? '',
    phone: u?.phone ?? '',
    userType: String(u?.group ?? 'USER'),
    departmentNames: deptNames,
    licenseNumber: u?.driverSpecificData?.licenseNumber ?? '',
    licenseType: u?.driverSpecificData?.licenseType ?? '',
    expireDate
  };
};

const toOption = (user: User): UserOption => ({
  value: String(user.id),
  label: user.empNumber ? `${user.empNumber} - ${user.name}` : user.name,
  user
});

// Minor staff only (Responsible)
const loadMinorStaffOptions = async (inputValue: string): Promise<UserOption[]> => {
  const payload = {
    filterType: inputValue.trim() ? 'nameOrEmpNo' : '',
    filterValue: inputValue.trim() || '',
    date: ''
  };

  const options: UserOption[] = [];
  const seenById = new Set<number>();

  try {
    let page = 1;
    while (true) {
      const res = await apiDefinitions.getUsers(page, payload);
      if (res?.data?.status !== 'S1000') {
        showError(res?.data?.statusDescription || 'Failed to fetch users');
        break;
      }

      const usersArr: any[] = res?.data?.data?.users ?? [];
      if (!usersArr.length) break;

      usersArr
        .filter(u => String(u?.group ?? '').toUpperCase() === 'MINOR_STAFF')
        .map(mapApiUserToDTO)
        .filter((u): u is User => !!u)
        .forEach(u => {
          if (!seenById.has(u.id)) {
            seenById.add(u.id);
            options.push(toOption(u));
          }
        });

      page += 1;
    }
  } catch (e: any) {
    showError(e?.response?.data?.statusDescription || 'Failed to fetch users');
  }

  return options;
};

// All users (Accountable)
const loadAllUsersOptions = async (inputValue: string): Promise<UserOption[]> => {
  const payload = {
    filterType: inputValue.trim() ? 'nameOrEmpNo' : '',
    filterValue: inputValue.trim() || '',
    date: ''
  };

  const options: UserOption[] = [];
  const seenById = new Set<number>();

  try {
    let page = 1;
    while (true) {
      const res = await apiDefinitions.getUsers(page, payload);
      if (res?.data?.status !== 'S1000') {
        showError(res?.data?.statusDescription || 'Failed to fetch users');
        break;
      }

      const usersArr: any[] = res?.data?.data?.users ?? [];
      if (!usersArr.length) break;

      usersArr
        .map(mapApiUserToDTO)
        .filter((u): u is User => !!u)
        .forEach(u => {
          if (!seenById.has(u.id)) {
            seenById.add(u.id);
            options.push(toOption(u));
          }
        });

      page += 1;
    }
  } catch (e: any) {
    showError(e?.response?.data?.statusDescription || 'Failed to fetch users');
  }

  return options;
};

const fetchOneUserById = async (id: number | null): Promise<User | null> => {
  if (id == null) return null;
  try {
    const byId = await apiDefinitions.getUsers(1, { filterType: 'id', filterValue: String(id), date: '' });
    if (byId?.data?.status === 'S1000') {
      const usersArr: any[] = byId?.data?.data?.users ?? [];
      const match = usersArr.find(u => Number(u?.id) === Number(id));
      const dto = mapApiUserToDTO(match);
      if (dto) return dto;
    }
  } catch { /* ignore */ }

  try {
    let page = 1;
    while (true) {
      const res = await apiDefinitions.getUsers(page, { filterType: '', filterValue: '', date: '' });
      if (res?.data?.status !== 'S1000') break;
      const arr: any[] = res?.data?.data?.users ?? [];
      if (!arr.length) break;
      const found = arr.find(u => Number(u?.id) === Number(id));
      if (found) {
        const dto = mapApiUserToDTO(found);
        if (dto) return dto;
      }
      page += 1;
    }
  } catch { /* ignore */ }
  return null;
};

/* -------------------------- Scope resolver (deepest wins) -------------------------- */
const resolveScopeIds = (
  locationId: number | null,
  buildingId: number | null,
  floorId: number | null,
  roomId: number | null
) => {
  if (roomId)      return { locationId: null, buildingId: null, floorId: null, roomId };
  if (floorId)     return { locationId: null, buildingId: null, floorId,      roomId: null };
  if (buildingId)  return { locationId: null, buildingId,      floorId: null, roomId: null };
  if (locationId)  return { locationId,      buildingId: null, floorId: null, roomId: null };
  return null;
};

/* ------------------------------ Component ------------------------------ */

const UpdateMaintenanceTaskModule: React.FC<Props> = ({
  show, onHide, onSuccess, task, locations
}) => {
  // busy
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Basic info
  const [description, setDescription] = useState(task.description);
  const [status, setStatus] = useState<TaskStatus>('ACTIVE');

  // Scope ids and local options
  const [selectedLocationId, setSelectedLocationId] = useState<number | null>(task.locationId);
  const [selectedBuildingId, setSelectedBuildingId] = useState<number | null>(task.buildingId);
  const [selectedFloorId, setSelectedFloorId] = useState<number | null>(task.floorId);
  const [selectedRoomId, setSelectedRoomId] = useState<number | null>(task.roomId);

  const [buildingsLocal, setBuildingsLocal] = useState<BuildingDTO[]>([]);
  const [floorsLocal, setFloorsLocal] = useState<FloorDTO[]>([]);
  const [roomsLocal, setRoomsLocal] = useState<RoomDTO[]>([]);

  // Dates
  const [startDateTime, setStartDateTime] = useState<Date | null>(task.startDateTime ? new Date(task.startDateTime) : new Date());
  const [endDateTime, setEndDateTime] = useState<Date | null>(task.endDateTime ? new Date(task.endDateTime) : null);

  // Schedule
  const [isRecurring, setIsRecurring] = useState<boolean>(Boolean(task.isRecurring ?? task.recurring ?? false));
  const [scheduleType, setScheduleType] = useState<ScheduleType | ''>((task.scheduleType as ScheduleType | '') || '');
  const [selectedDays, setSelectedDays] = useState<DayName[]>(task.customDays ? task.customDays.split(',') as DayName[] : []);
  const [occurrenceCount, setOccurrenceCount] = useState<number | null>(task.occurrenceCount ?? null);
  const [occurrenceUnit, setOccurrenceUnit] = useState<OccurrenceUnit>('DAYS');

  // Assignment
  const [isPermanentResponsibleAllocate, setIsPermanentResponsibleAllocate] = useState<boolean>(task.permanentResponsibleAllocate);
  const [responsiblePersons, setResponsiblePersons] = useState<User[]>(task.responsiblePersons || []);
  const [accountablePersons, setAccountablePersons] = useState<User[]>(task.accountablePersons || []);

  /* -------------------------- Cascading fetchers -------------------------- */

  const fetchBuildingsForLocation = async (locId: number) => {
    try {
      const res = await apiDefinitions.getBuildingsByLocationId(locId);
      if (res.status === 200) {
        setBuildingsLocal(res.data?.data || []);
      } else {
        showError(res.data?.statusDescription || 'Failed to fetch buildings');
      }
    } catch (e: any) {
      showError(e?.response?.data?.statusDescription || 'Failed to fetch buildings');
    }
  };
  const fetchFloorsForBuilding = async (bId: number) => {
    try {
      const res = await apiDefinitions.getFloorsByBuildingId(bId);
      if (res.status === 200) {
        setFloorsLocal(res.data?.data || []);
      } else {
        showError(res.data?.statusDescription || 'Failed to fetch floors');
      }
    } catch (e: any) {
      showError(e?.response?.data?.statusDescription || 'Failed to fetch floors');
    }
  };
  const fetchRoomsForFloor = async (fId: number) => {
    try {
      const res = await apiDefinitions.getRoomsByFloorId(fId);
      if (res.status === 200) {
        setRoomsLocal(res.data?.data || []);
      } else {
        showError(res.data?.statusDescription || 'Failed to fetch rooms');
      }
    } catch (e: any) {
      showError(e?.response?.data?.statusDescription || 'Failed to fetch rooms');
    }
  };

  /* -------------------------- REFRESH from getMaintenanceTaskById -------------------------- */

  useEffect(() => {
    if (!show || !task?.id) return;

    const loadFreshTask = async () => {
      if (!checkPermission('ROLE_ADD_TASK')) {
        showError('You do not have permission to update maintenance tasks.');
        onHide();
        return;
      }

      setIsLoading(true);
      try {
        const res = await apiDefinitions.getMaintenanceTaskById(task.id); // <-- refresh by ID
        if (res?.data?.status !== 'S1000' || !res?.data?.data) {
          showError(res?.data?.statusDescription || 'Failed to load maintenance task');
          setIsLoading(false);
          return;
        }

        const t: MaintenanceTaskDTO = res.data.data;

        // Basic
        setDescription(t.description ?? '');
        setStatus(t.status as TaskStatus);

        // Dates
        setStartDateTime(t.startDateTime ? new Date(t.startDateTime) : new Date());
        setEndDateTime(t.endDateTime ? new Date(t.endDateTime) : null);

        // Recurrence
        setIsRecurring(Boolean(t.isRecurring ?? t.recurring ?? false));
        setScheduleType((t.scheduleType ?? '') as ScheduleType | '');
        setSelectedDays(t.customDays ? t.customDays.split(',') as DayName[] : []);
        setOccurrenceCount(t.occurrenceCount ?? null);
        setOccurrenceUnit(t.customType as OccurrenceUnit ?? 'DAYS');

        // Assignment
        setIsPermanentResponsibleAllocate(Boolean(t.permanentResponsibleAllocate));
        setResponsiblePersons(t.responsiblePersons || []);
        setAccountablePersons(t.accountablePersons || []);

        // Scope (and cascade)
        setSelectedLocationId(t.locationId ?? null);
        setSelectedBuildingId(null);
        setSelectedFloorId(null);
        setSelectedRoomId(null);

        if (t.locationId) {
          await fetchBuildingsForLocation(t.locationId);
          if (t.buildingId) {
            setSelectedBuildingId(t.buildingId);
            await fetchFloorsForBuilding(t.buildingId);
            if (t.floorId) {
              setSelectedFloorId(t.floorId);
              await fetchRoomsForFloor(t.floorId);
              if (t.roomId) {
                setSelectedRoomId(t.roomId);
              }
            }
          }
        }

      } catch (e: any) {
        showError(e?.response?.data?.statusDescription || 'Error loading maintenance task');
      } finally {
        setIsLoading(false);
      }
    };

    loadFreshTask();
  }, [show, task?.id]);

  /* -------------------------- Schedule interactions -------------------------- */

  const toggleDay = (day: DayName) => {
    setSelectedDays(prev => prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day]);
  };

  const onChangeScheduleType = (v: ScheduleType | '') => {
    setScheduleType(v);

    if (!v) {
      setSelectedDays([]);
      setOccurrenceUnit('DAYS');
      return;
    }

    if (v === 'WEEKDAYS') {
      setSelectedDays(WEEKDAY_SET);
      setOccurrenceUnit('DAYS');
      return;
    }
    if (v === 'WEEKENDS') {
      setSelectedDays(WEEKEND_SET);
      setOccurrenceUnit('DAYS');
      return;
    }
    if (v === 'WEEKLY') {
      const jsIdx = new Date().getDay(); // 0..6 (Sun..Sat)
      const mapIdx = [6, 0, 1, 2, 3, 4, 5];    // map JS index -> our DAYS index
      const day = DAYS[mapIdx[jsIdx]] as DayName;
      setSelectedDays([day]);
      setOccurrenceUnit('WEEKS');
      return;
    }
    if (v === 'CUSTOM') {
      if (selectedDays.length === 0) setSelectedDays(['Monday']);
      if (!occurrenceUnit) setOccurrenceUnit('WEEKS');
      return;
    }

    // DAILY / MONTHLY / YEARLY
    setSelectedDays([]);
    if (v === 'DAILY')   setOccurrenceUnit('DAYS');
    if (v === 'MONTHLY') setOccurrenceUnit('MONTHS');
    if (v === 'YEARLY')  setOccurrenceUnit('YEARS');
  };

  /* ------------------------------- Submit ------------------------------- */

  const handleSubmit = async () => {
    if (!checkPermission('ROLE_ADD_TASK')) {
      showError('You do not have permission to update maintenance tasks.');
      return;
    }

    if (!description || !startDateTime) {
      showError('Please fill in required fields (Description and Start Date & Time).');
      return;
    }

    if (status !== 'ACTIVE' && status !== 'INACTIVE') {
      showError('Invalid status. Must be ACTIVE or INACTIVE.');
      return;
    }

    const scope = resolveScopeIds(
      selectedLocationId, selectedBuildingId, selectedFloorId, selectedRoomId
    );
    if (!scope) {
      showError('Please select a location/building/floor/room (deepest selection wins).');
      return;
    }

        if (isPermanentResponsibleAllocate && (!responsiblePersons || responsiblePersons.length === 0 || !accountablePersons || accountablePersons.length === 0)) {
      showError('Please select both responsible and accountable persons.');
      return;
    }

    if (
      isPermanentResponsibleAllocate &&
      responsiblePersons.some(rp => accountablePersons.includes(rp))
    ) {
      showError('Responsible and Accountable persons must be different users.');
      return;
    }

    // Build customDays/customDaysOfWeek like your API expects
    let customDaysStr: string | null = null;
    if (isRecurring) {
      if (scheduleType === 'WEEKDAYS') customDaysStr = WEEKDAY_SET.join(',');
      else if (scheduleType === 'WEEKENDS') customDaysStr = WEEKEND_SET.join(',');
      else if (['WEEKLY', 'CUSTOM'].includes(scheduleType)) customDaysStr = selectedDays.join(',');
    }

    const payload: any = {
      id: task.id,
      description,
      startDateTime: startDateTime?.toISOString(),
      endDateTime: endDateTime?.toISOString() || null,

      // scope
      locationId: scope.locationId,
      buildingId: scope.buildingId,
      floorId: scope.floorId,
      roomId: scope.roomId,

      // schedule
      isRecurring,
      scheduleType: isRecurring ? (scheduleType || null) : null,
      customDays: isRecurring ? customDaysStr : null,           // server uses `customDays`
      customDaysOfWeek: null,                                   // keep null to avoid ambiguity
      occurrenceCount: isRecurring ? (occurrenceCount ?? null) : null,
      customType: isRecurring && scheduleType === 'CUSTOM' ? occurrenceUnit : null,

      // assignment
      isPermanentResponsibleAllocate,
      responsiblePersons: responsiblePersons.map(rp => rp.id), // Array of IDs
      accountablePersons: accountablePersons.map(ap => ap.id), // Array of IDs

      // status
      status,
    };

    setIsSaving(true);
    try {
      const res = await apiDefinitions.updateMaintenanceTask(payload);
      if (res?.data?.status === 'S1000') {
        showSuccess('Maintenance task updated successfully');
        onSuccess();
        onHide();
      } else {
        showError(res?.data?.statusDescription || 'Failed to update maintenance task');
      }
    } catch (e: any) {
      showError(e?.response?.data?.statusDescription || 'Error updating maintenance task');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Update Maintenance Task</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {isLoading ? (
          <div className="text-center my-3">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : (
          <Form>
            <Row>
              {/* Left Side */}
              <Col md={6}>
                {/* Description */}
                <Form.Group className="mb-3">
                  <Form.Label>Description</Form.Label>
                  <Form.Control
                    type="text"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    required
                    disabled={isSaving}
                  />
                </Form.Group>

                {/* Status */}
                <Form.Group className="mb-3">
                  <Form.Label>Status</Form.Label>
                  <Form.Select
                    value={status}
                    onChange={(e) => setStatus(e.target.value as TaskStatus)}
                    disabled={isSaving}
                  >
                    <option value="ACTIVE">ACTIVE</option>
                    <option value="INACTIVE">INACTIVE</option>
                  </Form.Select>
                </Form.Group>

                {/* Cascading selects */}
                <Form.Group className="mb-3">
                  <Form.Label>Location</Form.Label>
                  <Form.Select
                    value={selectedLocationId ?? ''}
                    onChange={(e) => setSelectedLocationId(Number(e.target.value) || null)}
                    disabled={locations.length === 0 || isSaving}
                  >
                    <option value="">Select a location</option>
                    {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                  </Form.Select>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Building</Form.Label>
                  <Form.Select
                    value={selectedBuildingId ?? ''}
                    onChange={(e) => setSelectedBuildingId(Number(e.target.value) || null)}
                    disabled={buildingsLocal.length === 0 || !selectedLocationId || isSaving}
                  >
                    <option value="">Select a building</option>
                    {buildingsLocal.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                  </Form.Select>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Floor</Form.Label>
                  <Form.Select
                    value={selectedFloorId ?? ''}
                    onChange={(e) => setSelectedFloorId(Number(e.target.value) || null)}
                    disabled={floorsLocal.length === 0 || !selectedBuildingId || isSaving}
                  >
                    <option value="">Select a floor</option>
                    {floorsLocal.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
                  </Form.Select>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Room</Form.Label>
                  <Form.Select
                    value={selectedRoomId ?? ''}
                    onChange={(e) => setSelectedRoomId(Number(e.target.value) || null)}
                    disabled={roomsLocal.length === 0 || !selectedFloorId || isSaving}
                  >
                    <option value="">Select a room</option>
                    {roomsLocal.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                  </Form.Select>
                </Form.Group>
              </Col>

              {/* Right Side */}
              <Col md={6}>
                {/* Dates */}
                <Form.Group className="mb-3">
                  <Form.Label>Start Date & Time</Form.Label>
                  <DatePicker
                    selected={startDateTime}
                    onChange={(d: Date | null) => setStartDateTime(d)}
                    showTimeSelect
                    dateFormat="yyyy-MM-dd HH:mm"
                    className="form-control"
                    required
                    disabled={isSaving}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>End Date & Time</Form.Label>
                  <DatePicker
                    selected={endDateTime}
                    onChange={(d: Date | null) => setEndDateTime(d)}
                    showTimeSelect
                    dateFormat="yyyy-MM-dd HH:mm"
                    className="form-control"
                    disabled={isSaving}
                  />
                </Form.Group>

                {/* Recurrence */}
                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    label="Is Recurring"
                    checked={isRecurring}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setIsRecurring(checked);
                      if (!checked) {
                        setScheduleType('');
                        setSelectedDays([]);
                        setOccurrenceCount(null);
                        setOccurrenceUnit('DAYS');
                      }
                    }}
                    disabled={isSaving}
                  />
                </Form.Group>

                {isRecurring && (
                  <>
                    <Form.Group className="mb-3">
                      <Form.Label>Schedule Type</Form.Label>
                      <Form.Select
                        value={scheduleType}
                        onChange={(e) => onChangeScheduleType((e.target.value as ScheduleType) || '')}
                        disabled={isSaving}
                      >
                        <option value="">Select schedule type</option>
                        {SCHEDULE_TYPES.map(t => (
                          <option key={t} value={t}>{t}</option>
                        ))}
                      </Form.Select>
                    </Form.Group>

                    {/* WEEKDAYS / WEEKENDS -> fixed chips */}
                    {(scheduleType === 'WEEKDAYS' || scheduleType === 'WEEKENDS') && (
                      <Form.Group className="mb-3">
                        <Form.Label>Days</Form.Label>
                        <div>
                          {(scheduleType === 'WEEKDAYS' ? WEEKDAY_SET : WEEKEND_SET).map(day => (
                            <span key={day} className="badge bg-secondary me-2">{day}</span>
                          ))}
                        </div>
                        <Form.Text muted>
                          These days will be sent automatically as <code>customDays</code>.
                        </Form.Text>
                      </Form.Group>
                    )}

                    {/* WEEKLY / CUSTOM -> choose specific days */}
                    {(scheduleType === 'WEEKLY' || scheduleType === 'CUSTOM') && (
                      <Form.Group className="mb-3">
                        <Form.Label>Select Days</Form.Label>
                        <div>
                          {DAYS.map(day => (
                            <Form.Check
                              key={day}
                              type="checkbox"
                              label={day}
                              checked={selectedDays.includes(day as DayName)}
                              onChange={() => toggleDay(day as DayName)}
                              disabled={isSaving}
                              inline
                            />
                          ))}
                        </div>
                      </Form.Group>
                    )}

                    {/* Occurrence Count (OPTIONAL) */}
                    <Form.Group className="mb-3">
                      <Form.Label>Occurrence Count (optional)</Form.Label>
                      <Form.Control
                        type="number"
                        min={1}
                        value={occurrenceCount ?? ''}
                        onChange={(e) => setOccurrenceCount(e.target.value ? Number(e.target.value) : null)}
                        placeholder="e.g., 2"
                        disabled={isSaving}
                      />
                    </Form.Group>

                    {/* CUSTOM cadence */}
                    {scheduleType === 'CUSTOM' && (
                      <Form.Group className="mb-3" style={{ width: 220 }}>
                        <Form.Label>Occurrence Unit</Form.Label>
                        <Form.Select
                          value={occurrenceUnit}
                          onChange={(e) => setOccurrenceUnit(e.target.value as OccurrenceUnit)}
                          disabled={isSaving}
                        >
                          {OCCURRENCE_UNITS.map(u => <option key={u} value={u}>{u}</option>)}
                        </Form.Select>
                      </Form.Group>
                    )}
                  </>
                )}

                {/* Responsible / Accountable */}
                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    label="Permanent Responsible Allocation"
                    checked={isPermanentResponsibleAllocate}
                    onChange={(e) => {
                      setIsPermanentResponsibleAllocate(e.target.checked);
                      if (!e.target.checked) {
                        setResponsiblePersons([]);
                        setAccountablePersons([]);
                      }
                    }}
                    disabled={isSaving}
                  />
                </Form.Group>

                {isPermanentResponsibleAllocate && (
                  <>
                    <Form.Group className="mb-3">
                      <Form.Label>Responsible Person(s)</Form.Label>
                      <AsyncSelect<UserOption, true, GroupBase<UserOption>>
                        cacheOptions
                        defaultOptions
                        loadOptions={loadMinorStaffOptions}
                        onChange={(selectedOptions) => {
                          const users = selectedOptions ? selectedOptions.map(opt => opt.user) : [];
                          setResponsiblePersons(users);
                        }}
                        placeholder="Search by EMP No. or Name"
                        value={responsiblePersons.map(u => ({
                          value: String(u.id),
                          label: `${u.empNumber} - ${u.name}`,
                          user: u
                        }))}
                        getOptionValue={(o) => o.value}
                        getOptionLabel={(o) => o.label}
                        isMulti
                        isClearable
                        isDisabled={isSaving}
                        noOptionsMessage={() => 'No minor staff found'}
                        loadingMessage={() => 'Loading minor staff...'}
                        menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                        styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Accountable Person(s)</Form.Label>
                      <AsyncSelect<UserOption, true, GroupBase<UserOption>>
                        cacheOptions
                        defaultOptions
                        loadOptions={loadAllUsersOptions}
                        onChange={(selectedOptions) => {
                          const users = selectedOptions ? selectedOptions.map(opt => opt.user) : [];
                          setAccountablePersons(users);
                        }}
                        placeholder="Search by EMP No. or Name"
                        value={accountablePersons.map(u => ({
                          value: String(u.id),
                          label: `${u.empNumber} - ${u.name}`,
                          user: u
                        }))}
                        getOptionValue={(o) => o.value}
                        getOptionLabel={(o) => o.label}
                        isMulti
                        isClearable
                        isDisabled={isSaving}
                        noOptionsMessage={() => 'No users found'}
                        loadingMessage={() => 'Loading users...'}
                        menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                        styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                      />
                    </Form.Group>
                  </>
                )}
              </Col>
            </Row>
          </Form>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={isSaving}>
          Close
        </Button>
        <Button variant="success" onClick={handleSubmit} disabled={isSaving || isLoading}>
          {isSaving ? 'Saving...' : 'Save'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default UpdateMaintenanceTaskModule;

